import { FC, memo } from 'react'
import cn from 'classnames'
import { ModalForm } from 'components'
import { Form } from 'forms/Form'
import { required } from 'forms/validators'
import { isCompanyLegal, isSoleLegal } from 'modules/customer/helpers'
import { FieldIMask } from 'src/forms'
import { FieldFormItem } from 'src/forms/FieldFormItem'
import { getIsCustomerAddressDetailed } from 'src/helpers/address'
import { FieldAddressSelect } from 'src/shared/forms/FieldAddressSelect'
import { FieldCompanySelect } from 'src/shared/forms/FieldCompanySelect'
import { FieldText } from 'src/shared/forms/FieldText'

import { INITIAL_VALUES } from './constants'
import { customerAddDecorator } from './decorators'
import { CustomerAddFormProps } from './types'

const CustomerAddForm: FC<CustomerAddFormProps> = (props) => {
  const { isCustomerLoading, onClose, submitCustomer } = props

  return (
    <Form<typeof INITIAL_VALUES>
      keepDirtyOnReinitialize
      decorators={customerAddDecorator}
      initialValues={INITIAL_VALUES}
      name="customerAdd"
      // TODO типы
      // @ts-ignore
      onSubmit={submitCustomer}
      render={(form) => {
        const selectedCompany = form.values.company
        const isDadataAddressDisable = form.values.addressSuggestorDisabled
        const isDadataCustomerDisable = form.values.companySuggestorDisabled

        const CompanySelected = !!selectedCompany.inn && (
          <div className="flex flex-col gap-2">
            {isCompanyLegal(selectedCompany?.inn) && (
              <div className="flex gap-2">
                <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">Юр. адрес</div>
                <div className="w-full text-[#1a1c1e]">{selectedCompany?.address || '—'}</div>
              </div>
            )}
            <div className="flex gap-2">
              <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">ИНН</div>
              <div className="w-full text-[#1a1c1e]">{selectedCompany?.inn || '—'}</div>
            </div>
            <div className="flex gap-2">
              <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">КПП</div>
              <div className="w-full text-[#1a1c1e]">{selectedCompany?.kpp || '—'}</div>
            </div>
          </div>
        )

        const CompanySelectView = (
          <FieldFormItem isDontWaitTouched label="Название компании / ИНН" name="company">
            <FieldCompanySelect
              handleErrorSearch={() => form.form.change('companySuggestorDisabled', true)}
              name="company"
              validate={(value) => required(value.id)}
              handleSearch={() => {
                form.form.change('companySuggestorDisabled', false)
                form.form.change('addressSuggestorDisabled', false)
              }}
            />
          </FieldFormItem>
        )
        const AddressSelectView = (
          <FieldFormItem isDontWaitTouched label="Юр. адрес" name="address">
            <FieldAddressSelect
              handleSearch={() => form.form.change('addressSuggestorDisabled', false)}
              name="address"
              handleErrorSearch={() => {
                form.form.change('addressSuggestorDisabled', true)
              }}
              // TODO скорее всего будет переиспользоваться
              validate={(value) =>
                value.fiasLevel
                  ? isSoleLegal(selectedCompany?.inn) &&
                    !getIsCustomerAddressDetailed(value.fiasLevel) &&
                    'Уточните адрес'
                  : required(value.fiasLevel)
              }
            />
          </FieldFormItem>
        )
        const CompanyDetailsView = (
          <>
            <FieldFormItem className="pb-2" label="Название компании" name="company.value">
              <FieldText
                name="company.value"
                testId="customers-page--customer-manual-add-form--name"
                validate={required}
              />
            </FieldFormItem>
            {isDadataAddressDisable && selectedCompany ? (
              <div className="flex gap-[16px] pb-[8px] text-[14px] text-[400]">
                <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">ИНН</div>
                <div className="w-full text-[#1a1c1e]">{selectedCompany?.inn || '—'}</div>
              </div>
            ) : (
              <FieldFormItem className={cn('pb-2 pt-4')} label="ИНН" name="company.inn">
                <FieldText
                  maxLength={12}
                  name="company.inn"
                  testId="customers-page--customer-manual-add-form--inn"
                  type="number"
                  validate={required}
                />
              </FieldFormItem>
            )}
            {isDadataAddressDisable && selectedCompany ? (
              <div className="flex gap-[16px] pb-[8px] text-[14px] text-[400]">
                <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">КПП</div>
                <div className="w-full text-[#1a1c1e]">{selectedCompany?.kpp || '—'}</div>
              </div>
            ) : (
              <FieldFormItem className={cn('pb-2 pt-4')} label="КПП" name="company.kpp">
                <FieldText
                  maxLength={9}
                  name="company.kpp"
                  testId="customers-page--customer-manual-add-form--kpp"
                  type="number"
                  // TODO скорее всего будет переиспользоваться
                  validate={(value: any) => (isCompanyLegal(form.values.company.inn) ? required(value) : undefined)}
                />
                {/* // TODO скорее всего будет переиспользоваться */}
                {!isCompanyLegal(form.values.company.inn) && (
                  <div className="pt-1 text-body-sm text-on-surface-variant">Необязательно для ИП</div>
                )}
              </FieldFormItem>
            )}
          </>
        )

        const AddressDetailsView = (
          <>
            <div className="grid grid-cols-2 gap-2 max-md:grid-cols-1">
              <FieldFormItem label="Почтовый индекс" name="address.zipCode">
                <FieldIMask
                  mask="000000"
                  name="address.zipCode"
                  testId="customers-page--customer-manual-add-form--zip-code"
                  validate={required}
                />
              </FieldFormItem>
              <FieldFormItem label="Название населенного пункта" name="address.localityName">
                <FieldText
                  name="address.localityName"
                  testId="customers-page--customer-manual-add-form--locality-name"
                  validate={required}
                />
              </FieldFormItem>
            </div>
            <div className="grid grid-cols-3 gap-2 max-md:grid-cols-1">
              <FieldFormItem label="Название улицы" name="address.street">
                <FieldText
                  name="address.street"
                  testId="customers-page--customer-manual-add-form--street"
                  validate={required}
                />
              </FieldFormItem>
              <FieldFormItem label="Номер дома, строение" name="address.house">
                <FieldText
                  name="address.house"
                  testId="customers-page--customer-manual-add-form--house"
                  validate={required}
                />
              </FieldFormItem>
              <FieldFormItem label="Квартира, офис" name="address.flat">
                <FieldText name="address.flat" testId="customers-page--customer-manual-add-form--flat" />
                {!form.values.address.flat?.length && (
                  <div className="pt-1 text-body-sm text-on-surface-variant">Необязательно</div>
                )}
              </FieldFormItem>
            </div>
          </>
        )

        return (
          <form name="customerAdd" onSubmit={form.handleSubmit}>
            <ModalForm
              isDialogType
              buttonLabel="Добавить"
              id="customerAdd"
              isLoading={isCustomerLoading}
              onClose={onClose}
              title="Добавление контрагента"
            >
              <FieldFormItem className="hidden" name="companySuggestorDisabled">
                <FieldText name="companySuggestorDisabled" />
              </FieldFormItem>
              <FieldFormItem className="hidden" name="addressSuggestorDisabled">
                <FieldText name="addressSuggestorDisabled" />
              </FieldFormItem>
              {isDadataCustomerDisable ? (
                <>
                  <div className="pb-[28px] max-md:pb-1">
                    К сожалению, поиск по названию компании или ИНН временно недоступен. Заполните поля вручную.
                  </div>
                  {CompanyDetailsView}
                  {AddressDetailsView}
                </>
              ) : (
                <>
                  {CompanySelectView}
                  {CompanySelected}
                  {isDadataAddressDisable ? (
                    <>{isSoleLegal(selectedCompany?.inn) && AddressDetailsView}</>
                  ) : (
                    <>{isSoleLegal(selectedCompany?.inn) && AddressSelectView}</>
                  )}
                </>
              )}
            </ModalForm>
          </form>
        )
      }}
    />
  )
}

export default memo(CustomerAddForm)
